import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import {
  loadCurrentUser,
  signIn as authSignIn,
  signOut as authSignOut,
  type SignInResult,
  type User,
} from '@/lib/auth'
import { getSupabase, isSupabaseEnabled } from '@/lib/supabase'

/**
 * Context de autenticación a nivel de plataforma.
 *
 * `loadCurrentUser()` es async (Supabase consulta API; local es síncrono
 * envuelto en Promise). Mantenemos un estado `status` con tres valores
 * para que los consumidores puedan distinguir:
 *
 *   - `loading`: estamos comprobando si hay sesión. Mostrar spinner.
 *   - `authenticated`: hay usuario; renderiza la app.
 *   - `unauthenticated`: no hay sesión; muestra LoginPage.
 *
 * Si Supabase está activo, suscribimos al `onAuthStateChange` para
 * recargar el usuario tras callbacks de magic link o sign-out desde
 * otra pestaña.
 */

type Status = 'loading' | 'authenticated' | 'unauthenticated'

interface AuthContextValue {
  user: User | null
  status: Status
  /** Inicia sesión (local o Supabase). Devuelve el resultado para que
   *  LoginPage pueda mostrar "Revisa tu email" si es magic link. */
  signIn: (email: string, name: string, dob?: string) => Promise<SignInResult>
  signOut: () => Promise<void>
  /** Recarga el usuario desde el backend. Útil cuando un proceso
   *  externo (ej. webhook de Stripe) acaba de modificar permisos
   *  del usuario y queremos reflejarlo en la UI sin recargar la
   *  página entera. */
  refresh: () => Promise<void>
  /** Indica si el backend actual es Supabase (UX condicional). */
  backendKind: 'local' | 'supabase'
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<Status>('loading')
  const backendKind: 'local' | 'supabase' = isSupabaseEnabled() ? 'supabase' : 'local'

  // Carga inicial: comprueba si hay sesión activa.
  useEffect(() => {
    let cancelled = false
    loadCurrentUser()
      .then(u => {
        if (cancelled) return
        setUser(u)
        setStatus(u ? 'authenticated' : 'unauthenticated')
      })
      .catch(() => {
        if (cancelled) return
        setUser(null)
        setStatus('unauthenticated')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Si Supabase está activo, escucha cambios de auth para reaccionar a
  // callbacks de magic link y sign-out cross-tab.
  useEffect(() => {
    if (backendKind !== 'supabase') return
    const supabase = getSupabase()
    if (!supabase) return
    // OJO: llamar a métodos de `supabase.auth` (p.ej. getSession, vía
    // loadCurrentUser) DENTRO de este callback puede provocar un deadlock
    // con el lock interno de GoTrue. En SIGNED_OUT eso cuelga el logout.
    // Por eso el callback es síncrono: el caso "sin sesión" se resuelve al
    // momento, y el enriquecimiento (cursos/rol) + bootstrap de tenant se
    // difieren con setTimeout(0) para ejecutarse fuera del lock.
    const { data } = supabase.auth.onAuthStateChange((evento, session) => {
      if (!session?.user) {
        // Si no se ha cerrado a proposito, es que la sesion ha
        // caducado. La pantalla de acceso lo dira en vez de
        // aparecer sin mas.
        if (evento !== 'SIGNED_OUT' && typeof window !== 'undefined') {
          window.sessionStorage.setItem('mw-sesion-caducada', '1')

          // Y donde estaba, salvo que ya estuviera en el acceso
          const donde = window.location.pathname + window.location.search
          if (!donde.startsWith('/login')) {
            window.sessionStorage.setItem('mw-volver-a', donde)
          }
        }
        setUser(null)
        setStatus('unauthenticated')
        return
      }
      setTimeout(() => {
        void (async () => {
          const u = await loadCurrentUser()
          setUser(u)
          setStatus(u ? 'authenticated' : 'unauthenticated')

          // Multi-tenant: tras autenticarse, asegurar que el user está
          // dado de alta en el tenant actual (resuelto del hostname/path).
          // Idempotente; si ya estaba, no hace nada.
          try {
            const { bootstrapUserInTenant, resolveTenantSlug, looksLikeSlug } =
              await import('@/lib/tenant')
            const candidate = resolveTenantSlug()
            if (looksLikeSlug(candidate)) {
              await bootstrapUserInTenant(candidate)
            }
          } catch (e) {
            // No bloquear el flow de auth por un fallo de bootstrap.
            console.warn('[auth] tenant bootstrap failed:', e)
          }
        })()
      }, 0)
    })
    return () => {
      data.subscription.unsubscribe()
    }
  }, [backendKind])

  const signIn = useCallback(async (email: string, name: string, dob?: string): Promise<SignInResult> => {
    const result = await authSignIn(email, name, dob)
    if (result.kind === 'signed-in') {
      setUser(result.user)
      setStatus('authenticated')
    }
    // En 'magic-link-sent' el estado se actualizará via onAuthStateChange
    // cuando el alumno pulse el enlace y vuelva al sitio.
    return result
  }, [])

  const signOut = useCallback(async () => {
    await authSignOut()
    setUser(null)
    setStatus('unauthenticated')
  }, [])

  const refresh = useCallback(async () => {
    try {
      const u = await loadCurrentUser()
      setUser(u)
      setStatus(u ? 'authenticated' : 'unauthenticated')
    } catch {
      /* swallow: refresh es best-effort, no debe romper la UI */
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, status, signIn, signOut, refresh, backendKind }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth() debe llamarse dentro de un <AuthProvider>')
  return ctx
}

/**
 * Como useAuth, pero devuelve null si no hay proveedor.
 *
 * Para lo que debe funcionar con y sin sesion: las calculadoras se
 * usan desde la web publica, donde nadie ha entrado todavia.
 */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext)
}
